import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { useDataSource } from "@/data/DataProvider";
import type { DataSource } from "@/data/DataSource";
import { useIdentity } from "@/auth/ClientAuthProvider";
import { useJourney } from "@/hooks/useJourney";
import { usePlan } from "@/hooks/usePlan";
import { usePresentation } from "@/hooks/usePresentation";
import { usePolicies } from "@/hooks/usePolicies";
import type { AssistantClient, AssistantContext, AssistantTurn, HandoffOffer, VoiceSession } from "./types";
import { aiConsent, hasVoiceConsent, saveAiConsent, type AiConsent } from "./consent";

export type HandoffState = "sending" | "sent" | "failed" | "dismissed";

export interface AssistantApi {
  /** False until a brain is wired for this mode (today: demo only). */
  available: boolean;
  context: AssistantContext | null;
  turns: AssistantTurn[];
  /** The answer being written right now. */
  streaming: string | null;
  sheetOpen: boolean;
  voiceOpen: boolean;
  openSheet(ask?: string): void;
  closeSheet(): void;
  openVoice(): void;
  closeVoice(): void;
  ask(text: string): Promise<void>;
  /** Clears the conversation. */
  reset(): void;
  /** Voice turns join the same conversation. */
  addTurn(turn: AssistantTurn): void;
  handoffs: Record<string, HandoffState>;
  sendHandoff(key: string, offer: HandoffOffer): Promise<void>;
  dismissHandoff(key: string): void;
  /** The voice session in progress, if any. */
  voiceSession: VoiceSession | null;
  /**
   * Starts a voice session. Call it straight from a tap handler: iOS only
   * allows the microphone and speech inside the tap. False if voice isn't
   * loaded yet (the screen then offers its own start button).
   */
  startVoice(): boolean;
  /** True in demo mode: copy that describes what is saved must say nothing is. */
  demo: boolean;
  /**
   * Voice is demo-only for now: the browser's speech recognition can send
   * audio to its maker, and live transcripts have nowhere to be kept yet.
   */
  voice: boolean;
  /** The client's answer about AI answers on this device (consent.ts). */
  aiConsent: AiConsent;
  /** The permission card is up: asked before the first question, or from "Turn on AI answers". */
  consentPending: boolean;
  /** Agree (AI answers) or Not now (basic answers, nothing sent); a question asked first is answered then. */
  answerConsent(allow: boolean): void;
  /** Shows the permission card again, from the basic answers note. */
  askAboutAi(): void;
}

const noop = () => {};
const UNAVAILABLE: AssistantApi = {
  available: false,
  context: null,
  turns: [],
  streaming: null,
  sheetOpen: false,
  voiceOpen: false,
  openSheet: noop,
  closeSheet: noop,
  openVoice: noop,
  closeVoice: noop,
  ask: async () => {},
  reset: noop,
  addTurn: noop,
  handoffs: {},
  sendHandoff: async () => {},
  dismissHandoff: noop,
  voiceSession: null,
  startVoice: () => false,
  demo: false,
  voice: false,
  aiConsent: null,
  consentPending: false,
  answerConsent: noop,
  askAboutAi: noop,
};

const Ctx = createContext<AssistantApi>(UNAVAILABLE);

export function useAssistant(): AssistantApi {
  return useContext(Ctx);
}

/**
 * Holds the one conversation, shared by the text sheet and voice mode. It
 * lives inside the signed-in shell, so signing out (or a different client
 * signing in) starts from nothing: one client's questions never sit next to
 * another's. VITE_ASSISTANT=off at build time hides it everywhere.
 */
export function AssistantProvider({ children }: { children: ReactNode }) {
  const source = useDataSource();
  if (import.meta.env.VITE_ASSISTANT === "off") return <Ctx.Provider value={UNAVAILABLE}>{children}</Ctx.Provider>;
  return <AssistantHost source={source}>{children}</AssistantHost>;
}

function AssistantHost({ source, children }: { source: DataSource; children: ReactNode }) {
  const identity = useIdentity();
  const { journey, team } = useJourney();
  const plan = usePlan();
  const presentation = usePresentation();
  const policies = usePolicies();
  const context = useMemo<AssistantContext>(
    () => ({
      identity,
      journey,
      plan: plan.data ?? null,
      presentation: presentation.data ?? null,
      policies: policies.data ?? [],
      team: team.data ?? [],
    }),
    [identity, journey, plan.data, presentation.data, policies.data, team.data],
  );
  const contextRef = useRef(context);
  contextRef.current = context;

  const [client, setClient] = useState<AssistantClient | null>(null);
  // Voice code is loaded up front so a tap can start a session without awaiting anything.
  const voiceFactory = useRef<typeof import("./engine/voice").createDemoVoice | null>(null);
  useEffect(() => {
    let live = true;
    const demo = source.mode === "demo";
    void Promise.all([import("./engine/client"), demo ? import("./engine/voice") : Promise.resolve(null)]).then(([c, v]) => {
      if (!live) return;
      voiceFactory.current = v ? v.createDemoVoice : null;
      setClient(c.createAssistant(source));
    });
    return () => {
      live = false;
    };
  }, [source]);

  const [turns, setTurns] = useState<AssistantTurn[]>([]);
  const turnsRef = useRef(turns);
  turnsRef.current = turns;
  const [streaming, setStreaming] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [handoffs, setHandoffs] = useState<Record<string, HandoffState>>({});

  // A different demo persona is a different client: start a fresh conversation.
  useEffect(() => {
    setTurns([]);
    setHandoffs({});
  }, [identity.leadId]);

  const addTurn = useCallback((turn: AssistantTurn) => setTurns((t) => [...t, turn]), []);

  // Nothing goes to the AI provider without the client's yes: the first question waits on the card.
  const [consent, setConsent] = useState<AiConsent>(aiConsent);
  const [consentFor, setConsentFor] = useState<string | null>(null);

  const ask = useCallback(
    async (text: string) => {
      const q = text.trim();
      if (!q || !client) return;
      if (aiConsent() === null) {
        setConsentFor(q);
        return;
      }
      const user: AssistantTurn = { id: `u-${Date.now()}`, role: "user", text: q, channel: "text", createdAt: new Date().toISOString(), sources: [], handoff: null, disclaimer: null };
      const history = turnsRef.current;
      setTurns((t) => [...t, user]);
      setStreaming("");
      try {
        for await (const ev of client.ask({ text: q, history, context: contextRef.current, channel: "text" })) {
          if (ev.type === "delta") setStreaming((s) => (s ?? "") + ev.text);
          else setTurns((t) => [...t, ev.turn]);
        }
      } catch {
        setTurns((t) => [
          ...t,
          { id: `e-${Date.now()}`, role: "assistant", text: "Sorry, I couldn't answer that just now. Try again, or message your team.", channel: "text", createdAt: new Date().toISOString(), sources: [], handoff: null, disclaimer: null },
        ]);
      } finally {
        setStreaming(null);
      }
    },
    [client],
  );

  const sendHandoff = useCallback(
    async (key: string, offer: HandoffOffer) => {
      if (!client) return;
      setHandoffs((h) => ({ ...h, [key]: "sending" }));
      try {
        await client.requestHandoff({ offer, context: contextRef.current });
        setHandoffs((h) => ({ ...h, [key]: "sent" }));
      } catch {
        setHandoffs((h) => ({ ...h, [key]: "failed" }));
      }
    },
    [client],
  );

  const [voiceSession, setVoiceSession] = useState<VoiceSession | null>(null);
  const sessionRef = useRef<VoiceSession | null>(null);
  const startVoice = useCallback((): boolean => {
    const create = voiceFactory.current;
    if (!client || !create) return false;
    sessionRef.current?.end();
    const session = create({ client, context: () => contextRef.current, history: () => turnsRef.current, useMic: true });
    session.start();
    sessionRef.current = session;
    setVoiceSession(session);
    return true;
  }, [client]);
  const endVoice = useCallback(() => {
    sessionRef.current?.end();
    sessionRef.current = null;
    setVoiceSession(null);
  }, []);
  useEffect(() => () => sessionRef.current?.end(), []);

  useEffect(() => {
    if (client && (sheetOpen || voiceOpen)) client.prepare?.();
  }, [client, sheetOpen, voiceOpen]);

  // Deep links: ?ask=<question> opens the sheet and asks it; ?voice=1 opens voice.
  const [params, setParams] = useSearchParams();
  const [pendingAsk, setPendingAsk] = useState<string | null>(null);
  useEffect(() => {
    const q = params.get("ask");
    const voice = params.get("voice");
    if (!q && !voice) return;
    if (q) {
      setPendingAsk(q);
      setSheetOpen(true);
    }
    if (voice && source.mode === "demo") setVoiceOpen(true);
    const next = new URLSearchParams(params);
    next.delete("ask");
    next.delete("voice");
    setParams(next, { replace: true });
  }, [params, setParams, source.mode]);
  // Asked before the brain finished loading: answer as soon as it has.
  useEffect(() => {
    if (client && pendingAsk) {
      setPendingAsk(null);
      void ask(pendingAsk);
    }
  }, [client, pendingAsk, ask]);

  const api = useMemo<AssistantApi>(
    () => ({
      available: true,
      context,
      turns,
      streaming,
      sheetOpen,
      voiceOpen,
      openSheet: (q?: string) => {
        setSheetOpen(true);
        if (q) setPendingAsk(q);
      },
      closeSheet: () => setSheetOpen(false),
      openVoice: () => {
        if (source.mode !== "demo") return;
        setVoiceOpen(true);
        // Already agreed on this device: start inside this same tap.
        if (hasVoiceConsent()) startVoice();
      },
      closeVoice: () => {
        endVoice();
        setVoiceOpen(false);
      },
      ask,
      reset: () => {
        setTurns([]);
        setHandoffs({});
      },
      addTurn,
      handoffs,
      sendHandoff,
      dismissHandoff: (key: string) => setHandoffs((h) => ({ ...h, [key]: "dismissed" })),
      voiceSession,
      startVoice,
      demo: source.mode === "demo",
      voice: source.mode === "demo",
      aiConsent: consent,
      consentPending: consentFor !== null,
      answerConsent: (allow: boolean) => {
        saveAiConsent(allow ? "yes" : "no");
        setConsent(allow ? "yes" : "no");
        const q = consentFor;
        setConsentFor(null);
        if (q) void ask(q);
      },
      askAboutAi: () => setConsentFor(""),
    }),
    [source.mode, context, turns, streaming, sheetOpen, voiceOpen, ask, addTurn, handoffs, sendHandoff, voiceSession, startVoice, endVoice, consent, consentFor],
  );

  return <Ctx.Provider value={api}>{children}</Ctx.Provider>;
}
